import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import ApiError from "./external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiError";

const AUTH_ERROR_STATUS_CODES = [401, 403];

function notifyApiError(error: Error): void {
  if (error instanceof ApiError) {
    error.notifyError();
  }
}

/**
 * App-wide QueryClient. Import this from non-React modules (axios interceptors,
 * websocket helpers, fetchQuery bridges). Inside components and custom hooks,
 * prefer useQueryClient() so tests can inject their own client.
 *
 * Per-query overrides win: e.g. useQuery({ refetchOnWindowFocus: true }) on a
 * data card that should refresh when the user comes back to the tab.
 *
 * staleTime starts at 0 to preserve current fetch-every-time behaviour; tune per-query later.
 * There is no auth interceptor, so 401/403 must not be retried or expired sessions get hammered.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: notifyApiError,
  }),
  mutationCache: new MutationCache({
    onError: notifyApiError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: (n, err) =>
        n < 2 && !(err instanceof ApiError && AUTH_ERROR_STATUS_CODES.includes(err.apiResponse.getHttpStatusCode() as number)),
    },
  },
});

/** Drop cached queries/mutations so the next session cannot see the previous user's data. */
export function clearQueryClientOnLogout() {
  queryClient.clear();
}
